import { useTranslation } from 'react-i18next';
import { CircleCheck, Headset } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import type { SupportAttachment, SupportMessage, VendorConversation } from '../../../common/models/support.model';
import { MessageTimeline } from '../components/MessageTimeline';
import { Composer } from '../components/Composer';
import { TaskBadge } from '../components/TaskBadge';

/** One conversation: the tasks it raised, the messages, and a reply box. */
export function SupportThread({
  conversation,
  messages,
  upload,
  onSend,
  draft,
  onDraftChange,
}: {
  conversation?: VendorConversation;
  messages?: SupportMessage[];
  upload: (file: File, onProgress: (f: number) => void) => Promise<SupportAttachment>;
  onSend: (payload: { body: string; attachments: SupportAttachment[] }) => Promise<void>;
  draft?: string;
  onDraftChange?: (text: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {conversation && conversation.tasks.length > 0 && (
        <div className="flex flex-col gap-1.5 border-b bg-muted/30 px-4 py-2.5">
          <span className="text-[11px] font-medium text-muted-foreground">{t('SUPPORT.THREAD.TRACKING')}</span>
          <div className="flex flex-wrap gap-1.5">
            {conversation.tasks.map((task) => <TaskBadge key={task._id} task={task} />)}
          </div>
        </div>
      )}

      {messages ? (
        <MessageTimeline
          messages={messages}
          viewer="vendor"
          emptyState={
            <div className="m-auto flex flex-col items-center gap-2 text-center text-sm text-muted-foreground">
              <Headset className="size-5" />
              {t('SUPPORT.THREAD.EMPTY')}
            </div>
          }
        />
      ) : (
        <div className="flex flex-1 flex-col justify-end gap-3 p-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-14 w-3/4 self-end" />
          <Skeleton className="h-10 w-1/2" />
        </div>
      )}

      <div className="flex flex-col gap-1.5 border-t p-3">
        {conversation?.status === 'resolved' && (
          <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
            <CircleCheck className="size-3.5 text-success" />
            {t('SUPPORT.THREAD.RESOLVED_HINT')}
          </p>
        )}
        <Composer
          upload={upload}
          onSend={onSend}
          placeholder={t('SUPPORT.THREAD.REPLY_PLACEHOLDER')}
          initialText={draft}
          onDraftChange={onDraftChange}
          disabled={!conversation}
        />
      </div>
    </div>
  );
}
